/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { BillOfQuantitiesDaoService } from '../../dao/bill-of-quantities-dao.service';
import { BillOfQuantitiesService } from '../bill-of-quantities.service';
import {
  BillOfQuantity,
  BillOfQuantityDTO,
} from 'src/app/_model/bill-of-quantity';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads BillOfQuantities
 */
@Injectable()
export class BillOfQuantitiesLogicService implements BillOfQuantitiesService {
  public billOfQuantities$ = new BehaviorSubject<BillOfQuantity[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private billOfQuantitiesDaoService: BillOfQuantitiesDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  public async updateBillOfQuantities(
    billOfQuantitiesDto: BillOfQuantityDTO[],
  ): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new bill of quantities from other IMS component',
      billOfQuantitiesDto,
    );

    try {
      let billOfQuantities: BillOfQuantity[] = [];

      for (const billOfQuantityDto of billOfQuantitiesDto) {
        billOfQuantities.push(BillOfQuantity.fromDto(billOfQuantityDto));
      }

      await this.billOfQuantitiesDaoService.saveBillOfQuantities(
        billOfQuantities,
      );
      await this.billOfQuantities$.next(billOfQuantities);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing BillOfQuantities: ',
        error?.message,
      );
      throw error;
    }
  }

  public getBillOfQuantityById(id: IMSId): BillOfQuantity {
    if (id) {
      return this.billOfQuantities$.getValue().find((boq) => {
        if (boq.id.imsId === id.imsId) {
          return true;
        }
        return false;
      });
    } else {
      return undefined;
    }
  }

  public getBillOfQuantityByServiceJobId(id: IMSId): BillOfQuantity {
    if (id) {
      return this.billOfQuantities$.getValue().find((boq) => {
        if (boq.servicejob.id.imsId === id.imsId) {
          return true;
        }
        return false;
      });
    } else {
      return undefined;
    }
  }

  // Resetable
  public async reset() {
    await this.billOfQuantitiesDaoService.reset();
  }

  public async clear() {
    await this.billOfQuantitiesDaoService.clear();
    this.billOfQuantities$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    this.billOfQuantities$.next([]);
  }

  public async postsync(): Promise<void> {}
  public async presync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalBillOfQuantities();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalBillOfQuantities() {
    try {
      this.logger.log(this.constructor.name, '- Load local BillOfQuantities');
      let billOfQuantities: BillOfQuantity[] =
        await this.billOfQuantitiesDaoService.getBillOfQuantities();
      this.logger.log(
        this.constructor.name,
        '- Loaded local BillOfQuantities:',
        billOfQuantities,
      );

      this.billOfQuantities$.next(billOfQuantities);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local BillOfQuantities: ',
        error?.message,
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {}
}
